import { useEffect, useState } from 'react'
import { useAuth } from '@/core/AuthContext'
import { revenueCat, type PremiumStatus } from './revenueCat'

const EMPTY_PREMIUM_STATUS: PremiumStatus = {
  customerInfo: null,
  entitlements: [],
  expirationDate: null,
  isActive: false,
  productIdentifier: null,
  willRenew: false,
}

export function usePremiumAccess() {
  const { user, userData } = useAuth()
  const cachedPremiumStatus = revenueCat.getCachedPremiumStatus()
  const [loading, setLoading] = useState(false)
  const [premiumStatus, setPremiumStatus] = useState<PremiumStatus>(cachedPremiumStatus ?? EMPTY_PREMIUM_STATUS)
  const [hasResolved, setHasResolved] = useState(Boolean(cachedPremiumStatus))

  const isSupportedPlatform = revenueCat.isSupportedPlatform()
  const shouldRequirePremium =
    userData?.role === 'client' &&
    userData?.onboardingComplete === true &&
    isSupportedPlatform

  const refresh = async () => {
    if (!user) {
      setPremiumStatus(EMPTY_PREMIUM_STATUS)
      setHasResolved(false)
      setLoading(false)
      return EMPTY_PREMIUM_STATUS
    }

    if (!shouldRequirePremium) {
      const nextStatus = revenueCat.getCachedPremiumStatus() ?? EMPTY_PREMIUM_STATUS
      setPremiumStatus(nextStatus)
      setHasResolved(Boolean(revenueCat.getCachedPremiumStatus()))
      setLoading(false)
      return nextStatus
    }

    setLoading(true)

    try {
      await revenueCat.prepareForUser(user.uid)
      const nextStatus = await revenueCat.getPremiumStatus()
      setPremiumStatus(nextStatus)
      setHasResolved(revenueCat.hasResolvedPremiumStatus())
      return nextStatus
    } catch (error) {
      console.error('Failed to refresh premium access:', error)
      setHasResolved(revenueCat.hasResolvedPremiumStatus())
      return revenueCat.getCachedPremiumStatus() ?? EMPTY_PREMIUM_STATUS
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const unsubscribe = revenueCat.subscribeToPremiumStatus((nextStatus) => {
      setPremiumStatus(nextStatus)
      setHasResolved(true)
    })

    if (!user) {
      setPremiumStatus(EMPTY_PREMIUM_STATUS)
      setHasResolved(false)
      setLoading(false)
      return unsubscribe
    }

    if (!shouldRequirePremium) {
      const nextStatus = revenueCat.getCachedPremiumStatus() ?? EMPTY_PREMIUM_STATUS
      setPremiumStatus(nextStatus)
      setHasResolved(Boolean(revenueCat.getCachedPremiumStatus()))
      setLoading(false)
      return unsubscribe
    }

    let isMounted = true
    const hasCachedStatus = revenueCat.hasResolvedPremiumStatus()

    setLoading(!hasCachedStatus)

    revenueCat
      .prepareForUser(user.uid)
      .then(() => revenueCat.getPremiumStatus())
      .then((nextStatus) => {
        if (!isMounted) return
        setPremiumStatus(nextStatus)
        setHasResolved(revenueCat.hasResolvedPremiumStatus())
      })
      .catch((error) => {
        if (!isMounted) return
        console.error('Failed to refresh premium access:', error)
        const fallbackStatus = revenueCat.getCachedPremiumStatus()
        if (fallbackStatus) {
          setPremiumStatus(fallbackStatus)
        }
        setHasResolved(revenueCat.hasResolvedPremiumStatus())
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false)
        }
      })

    return () => {
      isMounted = false
      unsubscribe()
    }
  }, [user, shouldRequirePremium])

  return {
    hasResolved,
    isPremiumActive: premiumStatus.isActive,
    isSupportedPlatform,
    loading,
    premiumStatus,
    refresh,
    shouldBlockApp: shouldRequirePremium && hasResolved && !premiumStatus.isActive,
    shouldRequirePremium,
  }
}
